import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, TrendingUp, Sparkles } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import logoDark from '@/assets/own-lg.png'
import logoLight from '@/assets/own-lg-light.png'

export default function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()
  const { actualTheme } = useTheme()
  const logo = actualTheme === 'dark' ? logoDark : logoLight

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(email, password)
      // Navigation will be handled by the App component based on user type
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-restaurant-primary/10 to-restaurant-secondary/10 p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo and Branding */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-2 text-restaurant-primary">
            <img src={logo} alt="Ordore Logo" className="h-[25px] sm:h-8 w-auto" />
            {/* <TrendingUp className="h-8 w-8" /> */}
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 dark:bg-violet-950/50 border border-violet-200 dark:border-violet-800 px-3 py-1 mt-1">
            <Sparkles className="h-3.5 w-3.5 animate-sparkle" />
            <span className="text-xs font-medium text-violet-700 dark:text-violet-300">Powered by ordore AI</span>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Sign In</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Demo Credentials */}
        {/* <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-sm">Demo Credentials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <strong>Admin:</strong>
              <div>Email: admin@restaurant.com</div>
              <div>Password: admin123</div>
            </div>
            <div>
              <strong>Restaurant Owner:</strong>
              <div>Email: owner@restaurant.com</div>
              <div>Password: owner123</div>
            </div>
          </CardContent>
        </Card> */}

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>© 2026 Techloom Innovation Pvt Ltd.</p>
          <p>All rights reserved.</p>
        </div>
      </div>
    </div>
  )
}